import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { WorkflowTemplate, WorkflowStep, Assignee, ApprovalMode } from "modules/workflowModule/types";
import { CatalogDoc, DocSource } from "modules/workflowModule/documentsStore";

/**
 * Szablony workflow (procedury akceptacji) — REST API (BE: WorkflowController),
 * trwałość per użytkownik i aktywny profil. Zastępuje wcześniejsze przechowywanie
 * w localStorage (klucz workflow_templates_v1).
 *
 * BE serializuje PascalCase; pola spoza kontraktu DTO (conditions, assignees,
 * approvalMode w etapie) BE przechowuje przez JsonExtensionData i zwraca w
 * oryginalnym (camelCase) zapisie — dlatego mapujemy tylko pola znane, a strukturę
 * przypisań/warunków przekazujemy „przezroczyście".
 */

// ---------------------------------------------------------------------------
// Kształt DTO po stronie BE (PascalCase pól znanych + przezroczyste pola etapu)
// ---------------------------------------------------------------------------

interface WorkflowStepApi {
  Id?: string;
  Order: number;
  Name?: string;
  Description?: string;
  DocumentFormula?: string;
  Role?: string;
  Actions?: WorkflowStep["actions"];
  // przezroczyste (Extra po stronie BE) — wracają w camelCase
  conditions?: WorkflowStep["conditions"];
  assignees?: Assignee[];
  approvalMode?: WorkflowStep["approvalMode"];
}

interface WorkflowTemplateApi {
  Id?: string;
  Name?: string;
  DocumentType?: string;
  Description?: string;
  Steps?: WorkflowStepApi[];
  IsActive?: boolean;
  GenerateMetric?: boolean;
  NotifyOnComplete?: Assignee[];
}

// ---------------------------------------------------------------------------
// Mapowanie camelCase (FE) <-> PascalCase (BE)
// ---------------------------------------------------------------------------

const stepToApi = (s: WorkflowStep): WorkflowStepApi => ({
  Id: s.id,
  Order: s.order,
  Name: s.name,
  Description: s.description,
  DocumentFormula: s.documentFormula,
  Role: s.role,
  Actions: s.actions,
  conditions: s.conditions,
  assignees: s.assignees,
  approvalMode: s.approvalMode,
});

const templateToApi = (t: WorkflowTemplate): WorkflowTemplateApi => ({
  Id: t.id || undefined,
  Name: t.name,
  DocumentType: t.documentType,
  Description: t.description,
  IsActive: t.isActive,
  GenerateMetric: t.generateMetric ?? false,
  NotifyOnComplete: t.notifyOnComplete ?? [],
  Steps: (t.steps ?? []).map(stepToApi),
});

const stepFromApi = (s: WorkflowStepApi): WorkflowStep => ({
  id: s.Id ?? "",
  order: s.Order ?? 0,
  name: s.Name ?? "",
  description: s.Description,
  documentFormula: s.DocumentFormula,
  role: s.Role ?? "",
  actions: s.Actions ?? [],
  conditions: s.conditions ?? [],
  assignees: s.assignees ?? [],
  approvalMode: s.approvalMode ?? "any",
});

const templateFromApi = (t: WorkflowTemplateApi): WorkflowTemplate => ({
  id: t.Id ?? "",
  name: t.Name ?? "",
  documentType: t.DocumentType ?? "",
  description: t.Description,
  steps: (t.Steps ?? []).map(stepFromApi).sort((a, b) => a.order - b.order),
  isActive: t.IsActive ?? false,
  generateMetric: t.GenerateMetric ?? false,
  notifyOnComplete: t.NotifyOnComplete ?? [],
});

// ---------------------------------------------------------------------------
// Funkcje API
// ---------------------------------------------------------------------------

const getTemplatesApi = async (): Promise<WorkflowTemplate[]> => {
  const { data } = await axiosInstance.get<WorkflowTemplateApi[]>("/workflow/templates");
  return (data ?? []).map(templateFromApi);
};

const createTemplateApi = async (template: WorkflowTemplate): Promise<WorkflowTemplate> => {
  const { data } = await axiosInstance.post<WorkflowTemplateApi>(
    "/workflow/templates",
    templateToApi(template),
  );
  return templateFromApi(data);
};

const updateTemplateApi = async (template: WorkflowTemplate): Promise<WorkflowTemplate> => {
  const { data } = await axiosInstance.put<WorkflowTemplateApi>(
    `/workflow/templates/${template.id}`,
    templateToApi(template),
  );
  return templateFromApi(data);
};

const deleteTemplateApi = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/workflow/templates/${id}`);
};

// ---------------------------------------------------------------------------
// Katalog „Dokumenty podlegające akceptacji" (trwałość per użytkownik/profil;
// zastępuje localStorage workflow_documents_v1). Synchronizacja całej listy.
// ---------------------------------------------------------------------------

interface WorkflowDocumentApi {
  Id: string;
  Name: string;
  Description?: string;
  Source: string;
  FormType?: string;
  FileFormat?: string;
  WorkflowTitle?: string;
  IsActive: boolean;
  CreatedAt?: string;
}

const docFromApi = (a: WorkflowDocumentApi): CatalogDoc => ({
  id: a.Id,
  name: a.Name,
  description: a.Description,
  source: (a.Source as DocSource) ?? "form",
  formType: a.FormType,
  fileFormat: a.FileFormat,
  workflowTitle: a.WorkflowTitle,
  isActive: a.IsActive,
  createdAt: a.CreatedAt ?? new Date().toISOString(),
});

const docToApi = (d: CatalogDoc): WorkflowDocumentApi => ({
  Id: d.id,
  Name: d.name,
  Description: d.description,
  Source: d.source,
  FormType: d.formType,
  FileFormat: d.fileFormat,
  WorkflowTitle: d.workflowTitle,
  IsActive: d.isActive,
  CreatedAt: d.createdAt,
});

const getDocumentsApi = async (): Promise<CatalogDoc[]> => {
  const { data } = await axiosInstance.get<WorkflowDocumentApi[]>("/workflow/documents");
  return (data ?? []).map(docFromApi);
};

const syncDocumentsApi = async (docs: CatalogDoc[]): Promise<void> => {
  await axiosInstance.post("/workflow/documents/sync", { Documents: docs.map(docToApi) });
};

// ---------------------------------------------------------------------------
// Silnik workflow — przebiegi procedur akceptacji (instancje) i decyzje
// ---------------------------------------------------------------------------

export type WorkflowDecisionAction = "approve" | "reject" | "send_back";

export interface WorkflowAssignment {
  Id: string;
  AssigneeUserId: string;
  AssigneeLabel?: string | null;
  Decision: "pending" | "approved" | "rejected" | "sent_back" | "skipped";
  Comment?: string | null;
  DecidedAt?: string | null;
  IsMine: boolean;
}

export interface WorkflowInstanceStep {
  Index: number;
  Id?: string | null;
  Name?: string | null;
  Description?: string | null;
  DocumentFormula?: string | null;
  ApprovalMode: ApprovalMode;
  Actions: WorkflowDecisionAction[];
  State: "upcoming" | "current" | "done" | "skipped";
  Assignments: WorkflowAssignment[];
}

export interface WorkflowInstance {
  Id: string;
  DocumentType: string;
  DocumentId: string;
  TemplateId: string;
  TemplateName?: string | null;
  Status: "active" | "completed" | "rejected" | "returned" | "cancelled";
  CurrentStepIndex: number;
  Amount: number;
  RequesterUserId: string;
  RequesterLabel?: string | null;
  CreatedAt: string;
  CompletedAt?: string | null;
  Steps: WorkflowInstanceStep[];
  MyPendingAssignment?: WorkflowAssignment | null;
  MyAllowedActions: WorkflowDecisionAction[];
}

export interface WorkflowDecisionRequest {
  ItemId?: string;
  AssignmentId?: string;
  Action: WorkflowDecisionAction;
  Comment?: string;
}

export interface WorkflowDecisionResponse {
  Instance: WorkflowInstance;
  DocumentAccepted: boolean;
}

const getInstanceApi = async (
  documentType: string,
  documentId: string,
): Promise<WorkflowInstance | null> => {
  const { data } = await axiosInstance.get<WorkflowInstance | null>(
    `/workflow/instances/${documentType}/${documentId}`,
  );
  return data ?? null;
};

const decideApi = async (
  req: WorkflowDecisionRequest,
): Promise<WorkflowDecisionResponse> => {
  const { data } = await axiosInstance.post<WorkflowDecisionResponse>(
    "/workflow/decide",
    req,
  );
  return data;
};

export const WORKFLOW_DECISION_LABELS: Record<WorkflowDecisionAction, string> = {
  approve: "Zatwierdź",
  reject: "Odrzuć",
  send_back: "Zwróć do poprawy",
};

// ---------------------------------------------------------------------------
// Hooki React Query
// ---------------------------------------------------------------------------

const QUERY_KEY = "workflowTemplates";
const DOCS_QUERY_KEY = "workflowDocuments";
const INSTANCE_QUERY_KEY = "workflowInstance";

const workflowService = {
  useGetTemplates: () =>
    useCustomQuery<WorkflowTemplate[]>([QUERY_KEY], getTemplatesApi, { retry: false }),

  /** Stan przebiegu procedury akceptacji dokumentu (null = nie był w obiegu). */
  useInstance: (documentType: string, documentId: string | undefined) =>
    useCustomQuery<WorkflowInstance | null>(
      [INSTANCE_QUERY_KEY, documentType, documentId],
      () => getInstanceApi(documentType, documentId!),
      { enabled: !!documentId, retry: false },
    ),

  /** Decyzja na etapie; po sukcesie unieważnia instancje, skrzynki i listy zaangażowań. */
  useDecide: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<WorkflowDecisionResponse, WorkflowDecisionRequest>(decideApi, {
      onSuccess: () => {
        queryClient.invalidateQueries([INSTANCE_QUERY_KEY]);
        queryClient.invalidateQueries({
          predicate: (q) => {
            const k = String(q.queryKey[0] ?? "");
            return k.startsWith("acceptance") || k.startsWith("budget");
          },
        });
      },
    });
  },

  /**
   * Aktywny (lub pierwszy) szablon workflow dla typu/typów dokumentu — do metryk.
   * Zwraca null, dopóki dane się nie załadują albo brak dopasowania.
   */
  useActiveTemplate: (documentType: string | string[]): WorkflowTemplate | null => {
    const { data } = useCustomQuery<WorkflowTemplate[]>([QUERY_KEY], getTemplatesApi, {
      retry: false,
    });
    const types = Array.isArray(documentType) ? documentType : [documentType];
    const matching = (data ?? []).filter((t) => types.includes(t.documentType));
    return matching.find((t) => t.isActive) ?? matching[0] ?? null;
  },

  useCreateTemplate: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<WorkflowTemplate, WorkflowTemplate>(createTemplateApi, {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY]),
    });
  },

  useUpdateTemplate: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<WorkflowTemplate, WorkflowTemplate>(updateTemplateApi, {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY]),
    });
  },

  useDeleteTemplate: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<void, string>(deleteTemplateApi, {
      onSuccess: () => queryClient.invalidateQueries([QUERY_KEY]),
    });
  },

  // Katalog dokumentów podlegających akceptacji.
  useGetDocuments: () =>
    useCustomQuery<CatalogDoc[]>([DOCS_QUERY_KEY], getDocumentsApi, { retry: false }),

  useSyncDocuments: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<void, CatalogDoc[]>(syncDocumentsApi, {
      onSuccess: () => queryClient.invalidateQueries([DOCS_QUERY_KEY]),
    });
  },

  getTemplatesApi,
  getDocumentsApi,
};

export default workflowService;
